export const GMAIL_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly'
const GMAIL_API = 'https://gmail.googleapis.com/gmail/v1/users/me'

export interface GmailMessageRef {
  id: string
  threadId: string
}

export interface GmailMessageFile {
  id: string
  subject: string
  date: Date
  blob: Blob
  size: number
}

export interface GmailSizeEstimate {
  estimatedBytes: number
  sampledCount: number
  sampledBytes: number
}

export class GmailTokenExpiredError extends Error {}

async function gmailRequest<T>(token: string, path: string) {
  const response = await fetch(`${GMAIL_API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
  if (response.status === 401 || response.status === 403) throw new GmailTokenExpiredError('Google pidió renovar el permiso de Gmail. Vuelve a conectar para continuar.')
  if (!response.ok) throw new Error(`Gmail respondió ${response.status}.`)
  return response.json() as Promise<T>
}

export async function listGmailMessages(token: string, query: string, onPage?: (count: number) => void) {
  const messages: GmailMessageRef[] = []
  let pageToken: string | undefined
  do {
    const params = new URLSearchParams({ maxResults: '500', fields: 'nextPageToken,messages(id,threadId)' })
    if (query.trim()) params.set('q', query.trim())
    if (pageToken) params.set('pageToken', pageToken)
    const page = await gmailRequest<{ nextPageToken?: string; messages?: GmailMessageRef[] }>(token, `/messages?${params.toString()}`)
    messages.push(...(page.messages ?? []))
    onPage?.(messages.length)
    pageToken = page.nextPageToken
  } while (pageToken)
  return messages
}

async function getMessageSizeEstimate(token: string, id: string) {
  const message = await gmailRequest<{ sizeEstimate?: number }>(token, `/messages/${encodeURIComponent(id)}?format=minimal&fields=sizeEstimate`)
  return typeof message.sizeEstimate === 'number' && Number.isFinite(message.sizeEstimate) && message.sizeEstimate >= 0
    ? message.sizeEstimate
    : null
}

function distributedSample<T>(items: T[], maximum: number) {
  if (items.length <= maximum) return items
  const sample: T[] = []
  for (let position = 0; position < maximum; position += 1) {
    sample.push(items[Math.floor(position * (items.length - 1) / (maximum - 1))])
  }
  return sample
}

/**
 * Gmail only exposes `sizeEstimate` on an individual message. Reading every
 * message before a paid backup would be disproportionately slow for large
 * mailboxes, so we sample evenly across the already filtered inventory.
 */
export async function estimateGmailBytes(token: string, messages: GmailMessageRef[], onProgress?: (completed: number, total: number) => void): Promise<GmailSizeEstimate> {
  const sample = distributedSample(messages, 80)
  if (!sample.length) return { estimatedBytes: 0, sampledCount: 0, sampledBytes: 0 }

  let cursor = 0
  let completed = 0
  let sampledBytes = 0
  let successfulSamples = 0

  const worker = async () => {
    while (cursor < sample.length) {
      const current = sample[cursor]
      cursor += 1
      try {
        const bytes = await getMessageSizeEstimate(token, current.id)
        if (bytes !== null) {
          sampledBytes += bytes
          successfulSamples += 1
        }
      } catch (error) {
        if (error instanceof GmailTokenExpiredError) throw error
      } finally {
        completed += 1
        onProgress?.(completed, sample.length)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(4, sample.length) }, worker))
  if (!successfulSamples) throw new Error('Gmail no devolvió tamaños para estimar el espacio de estos correos.')

  return {
    estimatedBytes: Math.round((sampledBytes / successfulSamples) * messages.length),
    sampledCount: successfulSamples,
    sampledBytes,
  }
}

function base64UrlToBytes(value: string) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

function headerValue(raw: string, name: string) {
  const headerSection = raw.split(/\r?\n\r?\n/, 1)[0].replace(/\r?\n[ \t]+/g, ' ')
  const match = headerSection.match(new RegExp(`^${name}:\\s*(.+)$`, 'im'))
  return match?.[1]?.trim()
}

function safeSubject(value?: string) {
  const decoded = value?.replace(/=\?([^?]+)\?[bq]\?([^?]+)\?=/gi, (_match, _charset, encoded) => encoded.replace(/_/g, ' ')) ?? ''
  return decoded.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '·').replace(/\s+/g, ' ').trim().slice(0, 100) || 'sin-asunto'
}

export async function downloadGmailMessage(token: string, id: string): Promise<GmailMessageFile> {
  const message = await gmailRequest<{ id: string; raw: string; internalDate?: string; sizeEstimate?: number }>(token, `/messages/${encodeURIComponent(id)}?format=raw&fields=id,raw,internalDate,sizeEstimate`)
  const bytes = base64UrlToBytes(message.raw)
  const blob = new Blob([bytes], { type: 'message/rfc822' })
  const preview = new TextDecoder('utf-8', { fatal: false }).decode(bytes.slice(0, Math.min(bytes.length, 65536)))
  const parsedDate = headerValue(preview, 'Date')
  const date = parsedDate && !Number.isNaN(Date.parse(parsedDate)) ? new Date(parsedDate) : message.internalDate ? new Date(Number(message.internalDate)) : new Date()
  return { id: message.id, subject: safeSubject(headerValue(preview, 'Subject')), date, blob, size: blob.size }
}
